import type { Currency } from '@axo/config/@types/Currency.types';
import type { Locale } from '@axo/config/@types/Locales.types';
import { useApplicationStateStore } from '@axo/product/common/store/application-state';
import { FormControl } from '@axo/ui-core/components/form/FormControl';
import type { CurrencyInputProps } from '@axo/ui-core/components/input/CurrencyInput';
import { CurrencyInput } from '@axo/ui-core/components/input/CurrencyInput';
import { Slider, type SliderProps } from '@axo/ui-core/components/input/Slider';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Heading, Text } from '@axo/ui-core/components/typography';
import clsx from 'clsx';
import type { TOptions } from 'i18next';
import type { ChangeEvent } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';
import styles from './styles.module.scss';

type TextProps<T extends FieldValues> = Omit<SliderProps, 'name'> & {
  name: Path<T>;
  title: string;
  titleAsHeading?: boolean;
  hideLegend?: boolean;
  inputMode?: CurrencyInputProps['inputMode'];
  curConfig: {
    locale: Locale;
    currency: Currency;
    fractionDigits: number;
  };
  TOptions?: TOptions;
  'data-testid'?: string;
};

export const SliderField = <T extends FieldValues>({
  name,
  title,
  titleAsHeading = false,
  hideLegend = false,
  curConfig,
  inputMode = 'numeric',
  TOptions,
  'data-testid': testId,
  ...props
}: TextProps<T>) => {
  const namespace = useApplicationStateStore(
    useShallow((state) => state.i18n.namespace)
  );
  const { t } = useTranslation(namespace, TOptions);
  const { control } = useFormContext();
  const { locale, currency, fractionDigits } = curConfig;

  // TODO: replace with ConfigService
  const Currency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: fractionDigits,
  });

  const prefix = Currency.formatToParts().find(
    (part) => part.type === 'currency'
  )?.value;

  const parseCurrencyInput = (e: ChangeEvent<HTMLInputElement>): number =>
    isNaN(+e.target.value) ? 0 : +e.target.value;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        min: props.min,
        max: props.max,
      }}
      render={({
        field: { value, onChange, ref, ...restField },
        fieldState: { error, isTouched },
      }) => (
        <Stack gap="xs" className={styles.container}>
          <Stack
            direction="h"
            className={clsx(styles.stack, styles.sliderInput)}
          >
            {titleAsHeading ? (
              <>
                <Heading className={styles.heading} size="m">
                  {title}
                </Heading>
                <CurrencyInput
                  className={styles.headlineInput}
                  state={error ? 'error' : 'neutral'}
                  prefix={prefix}
                  inputMode={inputMode}
                  value={value}
                  onChange={(e) => onChange(parseCurrencyInput(e))}
                  onBlur={restField.onBlur}
                  _ref={ref}
                  data-testid={testId}
                />
              </>
            ) : (
              <>
                <FormControl.Label htmlFor={name}>{title}</FormControl.Label>
                <CurrencyInput
                  id={name}
                  className={styles.input}
                  prefix={prefix}
                  state={error ? 'error' : isTouched ? 'success' : 'neutral'}
                  inputMode={inputMode}
                  value={value}
                  onChange={(e) => onChange(parseCurrencyInput(e))}
                  onBlur={restField.onBlur}
                  _ref={ref}
                  data-testid={testId}
                />
              </>
            )}
          </Stack>
          <Slider
            {...props}
            {...restField}
            value={[value]}
            onValueChange={(val) => onChange(val[0])}
            className={styles.slider}
          />
          {!hideLegend && (
            <Stack direction="h" className={styles.stack}>
              <Text className={styles.sliderLegend}>
                {Currency.format(props.min ?? 0)}
              </Text>
              <Text className={styles.sliderLegend}>
                {Currency.format(props.max ?? 0)}
              </Text>
            </Stack>
          )}
          {error?.message && (
            <FormControl.Validation state="error">
              {t(error.message)}
            </FormControl.Validation>
          )}
        </Stack>
      )}
    ></Controller>
  );
};
